import React, { useState, useEffect, useContext } from "react";
import {
  AppBar,
  Typography,
  Button,
  IconButton,
  TextField,
  Box,
  Breadcrumbs,
  Autocomplete,
  InputAdornment,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { MOUdata } from "../../components/grid/GridData.tsx";
import { externalgetApi } from "../../services/externalApis.tsx";
import { SETTING_API_05 } from "../../api/ApiModel.tsx";
import ViewDataGrid from "../../components/grid/ViewDataGrid.tsx";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import { CON_STATUS_ACTIVE } from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";
import SystemAlert from "../../components/common/SystemAlert.tsx";
import {
  MeasuringUnitMessages,
  MessageCategory,
} from "../../utils/sys/Messages.tsx";
// Types and Interfaces
interface MeasuringUnit {
  immeasuringunitcode?: string | number;
  immeasuringunit?: string;
  measuringunitcode?: string | number;
  measuringunit?: string;
  descrip?: string;
  [key: string]: unknown;
}

interface UserParams {
  LOCCODE?: string;
  STAFFMAPLOCODE?: string;
  COMPCODE?: string;
  USERNAME?: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  onClick: (id: string | number) => void;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

interface ErrorState {
  [key: string]: string | undefined;
}

interface PageData {
  title: string;
  [key: string]: unknown;
}

interface Column {
  field: string;
  headerName: string;
  width?: number;
  flex?: number;
}

interface RowWithId extends MeasuringUnit {
  id: string | number;
}

function ViewMeasuringunit(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const { setPageData } = usePageData();
  
  const SYS_USER = useSelector((state: { user: UserState }) => state.user) || {};
  const { LOCCODE, STAFFMAPLOCODE, COMPCODE, USERNAME } =
    SYS_USER.USER_PARAM || {};

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filteredRows, setFilteredRows] = useState<MeasuringUnit[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<MessageCategory.SUCCESS | MessageCategory.ERROR | MessageCategory.WARNING>(MessageCategory.SUCCESS);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | number | null>(null);
  const [data, setData] = useState<MeasuringUnit[] | null>(null);
  const [error, setError] = useState<ErrorState>({});

  useEffect(() => {
    setPageData({
      title: "Measuring Unit",
    } as PageData);
  }, [setPageData]);

  const handleItemClick = (id: string | number, actionText: string): void => {
    if (actionText === "Edit") {
      const selectedItem = data?.find((item) => item.immeasuringunitcode === id);
      if (selectedItem) {
        navigate("/CreateMeasuringUnits", {
          state: { selectedItem, actionText },
        });
      }
    }
    if (actionText === "Delete") {
      setSelectedId(id);
      setOpenDeleteDialog(true);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    try {
      setIsLoading(true);

      // Using externaldeletecomApi - you'll need to import this properly
      // This is a placeholder - adjust based on your actual API function
      await fetch(`${SETTING_API_05}/${selectedId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          immeasuringunitcode: String(selectedId),
          ENUSER: USERNAME || "ss",
        }),
      });

      setData((prev) =>
        prev?.filter((row) => row.immeasuringunitcode !== selectedId) || null
      );
      setFilteredRows((prev) =>
        prev?.filter((row) => row.immeasuringunitcode !== selectedId) || []
      );

      setAlertMessage(MeasuringUnitMessages.success.delete);
      setAlertType(MessageCategory.SUCCESS);
      setAlertOpen(true);
    } catch (error) {
      const err = error as ApiError;
      console.error("DELETE API error:", err);

      const errorMessage = MeasuringUnitMessages.error.deleteFailed;

      setAlertMessage(errorMessage);
      setAlertType(MessageCategory.SUCCESS);
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
      setOpenDeleteDialog(false);
      setSelectedId(null);
    }
  };

  const cancelDelete = (): void => {
    setOpenDeleteDialog(false);
    setSelectedId(null);
  };

  const menuItems: MenuItem[] = [
    {
      text: "Edit",
      icon: <BorderColorOutlinedIcon />,
      onClick: (immeasuringunitcode: string | number) =>
        handleItemClick(immeasuringunitcode, "Edit"),
    },
    {
      text: "Delete",
      icon: <DeleteIcon />,
      onClick: (immeasuringunitcode: string | number) =>
        handleItemClick(immeasuringunitcode, "Delete"),
    },
  ];

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const result = (await externalgetApi(SETTING_API_05, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          STS: CON_STATUS_ACTIVE,
        })) as MeasuringUnit[];

        setData(result);
        setFilteredRows(result);
        console.log("result", result);
        setIsLoading(false);
      } catch (err) {
        setError(err as ErrorState);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [COMPCODE, STAFFMAPLOCODE]);

  // Handle search input change
  const handleSearchChange = (event: React.SyntheticEvent, value: string | null): void => {
    const searchValue = value || "";
    setSearchQuery(searchValue);

    // Filter rows based on search query
    const filtered = data?.filter((row) =>
      row.immeasuringunit?.toLowerCase().includes(searchValue.toLowerCase())
    );
    setFilteredRows(filtered || []);
  };

  const addToView = (): void => {
    navigate("/CreateMeasuringUnits");
  };

  // Prepare rows with id for the grid
  const rowsWithId: RowWithId[] = filteredRows.map((row) => ({
    ...row,
    id: row.immeasuringunitcode || Math.random(),
  }));

  // Placeholder processRowUpdate function
  const processRowUpdate = async (newRow: any): Promise<any> => {
    console.log("Row update attempted:", newRow);
    return newRow;
  };

  return (
    <>
      <Box sx={styles.box_breadcrumb_view}>
        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={styles.breadcrumb}
        >
          <Link
            underline="hover"
            sx={styles.breadcrumb_link}
          >
            Home
          </Link>
          <Link
            sx={styles.breadcrumb_link}
            underline="hover"
          >
            Settings
          </Link>
          <Link
            sx={styles.breadcrumb_link_page}
            underline="hover"
            aria-current="page"
          >
            Measuring Unit
          </Link>
        </Breadcrumbs>
      </Box>

      <Box sx={styles.box_page_container}>
        <AppBar sx={styles.appbar}>
          <Grid container spacing={2} sx={styles.appbar_grid_container}>
            <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 8, xl: 8 }}
              sx={styles.appbar_grid_container_col_1}
            >
              <Typography sx={styles.appbar_title}>{MOUdata.name}</Typography>
            </Grid>

            <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}
              sx={styles.appbar_grid_search}
            >
              <Autocomplete
                freeSolo
                id="act_appbarsearch"
                fullWidth
                disableClearable
                value={searchQuery}
                onInputChange={handleSearchChange}
                sx={styles.ipt_appbar_search_auto}
                options={data?.map((row) => row.immeasuringunit || "").filter(Boolean) || []}
                renderInput={(params) => {
                  const { id, ...restParams } = params;
                  return (
                    <TextField
                      id="txt_appbarsearch"
                      {...restParams}
                      placeholder="Search by..."
                      size="small"
                      sx={styles.ipt_appbar_search}
                      InputProps={{
                        ...restParams.InputProps,
                        type: "search",
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton 
                              id="btn_search"
                              sx={styles.btn_appbar_search_icon}
                            >
                              <SearchIcon id="ico_search" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  );
                }}
              />
              <Button
                id="btn_appbaradd"
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                size="small"
                sx={{ ...styles.btn_primary, ...styles.btn_appbar_add }}
                onClick={addToView}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </AppBar>

        <Box sx={styles.view_page_container}>
          <ViewDataGrid
            columns={MOUdata.columns}
            rows={rowsWithId}
            menuItems={menuItems}
            tabletitle={MOUdata.name}
            gridid="immeasuringunitcode"
            processRowUpdate={processRowUpdate}
          />
        </Box>
      </Box>

      <SystemAlert
        open={alertOpen}
        type={alertType}
        message={alertMessage}
        onClose={() => setAlertOpen(false)}
      />

      <Dialog
        id="dlg_delete"
        open={openDeleteDialog}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this Measuring Unit?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            id="btn_cancel"
            onClick={cancelDelete}
            sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            id="btn_delete"
            onClick={confirmDelete}
            color="error"
            sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ViewMeasuringunit;